import { initializeApp } from 'firebase/app'
import { AppCheck, ReCaptchaEnterpriseProvider, getToken, initializeAppCheck } from 'firebase/app-check'
import {
  Auth,
  RecaptchaVerifier,
  signInWithPhoneNumber as firebaseSignInWithPhoneNumber,
  getAuth,
  signInWithCustomToken,
} from 'firebase/auth'

import { buildFirebaseAuthorizationHeader } from '@consumer/graphql'

import indexedDBMonitor from './indexedDBMonitor'
import { logError } from './monitorClient'

/**
 * Legacy sentinel sent when App Check is unavailable. Predates CBP-3932 and is
 * preserved here only to keep this refactor behaviour-neutral.
 *
 * It does NOT let requests through: probing Esus staging directly, this value
 * returns 401 UnauthorizedException exactly like any other invalid token, so a
 * Firebase App Check outage fails every GraphQL call either way. Sending it
 * buys nothing over sending no header at all — the one upside is that it is
 * greppable in New Relic. Removing it is tracked separately; it must not be
 * copied to CMA or to any new caller.
 */
const APP_CHECK_FAILURE_TOKEN = 'appcheck-failure'

/** Token-refresh failures carrying these markers indicate browser-storage trouble. */
const INDEXED_DB_ERROR_MARKERS = ['IndexedDB', 'storage', 'QuotaExceededError']

const isIndexedDBError = (error: Error) => INDEXED_DB_ERROR_MARKERS.some(marker => error.message.includes(marker))

// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: `${import.meta.env.REACT_APP_FIREBASE_API_KEY}`,
  authDomain: `${import.meta.env.REACT_APP_FIREBASE_AUTH_DOMAIN}`,
  projectId: `${import.meta.env.REACT_APP_FIREBASE_PROJECT_ID}`,
  storageBucket: `${import.meta.env.REACT_APP_FIREBASE_STORAGE_BUCKET}`,
  messagingSenderId: `${import.meta.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID}`,
  appId: `${import.meta.env.REACT_APP_FIREBASE_APP_ID}`,
  measurementId: `${import.meta.env.REACT_APP_FIREBASE_MEASUREMENT_ID}`,
}

const isNotLocalDev = import.meta.env.REACT_APP_STAGE !== 'local'

let isFirebaseInitialized = false

interface Firebase {
  appCheck: AppCheck
  auth: Auth
}

const initializeFirebase = (): Firebase => {
  if (isNotLocalDev) {
    // Only initialize Firebase if not a local dev env
    const app = initializeApp(firebaseConfig)
    const appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(import.meta.env.REACT_APP_APPCHECK_SITE_KEY_ID ?? ''),
      isTokenAutoRefreshEnabled: true,
    })
    const auth = getAuth(app)

    // Set up auth state observer to track initialization
    auth.onAuthStateChanged(
      () => {
        isFirebaseInitialized = true
      },
      error => {
        // Handle Firebase Auth errors that might be related to IndexedDB
        logError('Firebase Auth state change error', { error })
        if (error?.message?.includes('IndexedDB') || error?.message?.includes('storage')) {
          indexedDBMonitor.triggerFailure(`Firebase Auth error: ${error.message}`)
        }
      }
    )

    return { appCheck, auth }
  } else {
    isFirebaseInitialized = true // Set to true for local dev
    return { appCheck: {} as AppCheck, auth: {} as Auth }
  }
}
export const { appCheck, auth }: Firebase = initializeFirebase()

/**
 * Sign in to Firebase with provided phone number. Sends a verification code to the phone number,
 * which the user must then enter to complete the sign-in process.
 * @param phoneNumber Phone number to sign in with via SMS.
 * @param recaptchaWrapperRef Wrapper around the div to attach the RecaptchaVerifier to.
 * @returns confirmation object from Firebase. This object is used to verify the code entered by the user.
 */
export const signInWithPhoneNumber = async (phoneNumber: string, recaptchaWrapperRef: HTMLDivElement) => {
  if (isNotLocalDev) {
    // Initialize the RecaptchaVerifier for Firebase phone authentication.
    // This requires an empty div with an id to attach the RecaptchaVerifier to, so we reset the div here to ensure it's empty.
    // The div must be loaded before calling this function.
    recaptchaWrapperRef.innerHTML = `<div id="recaptcha-container"></div>`
    const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
      size: 'invisible',
    })
    return await firebaseSignInWithPhoneNumber(auth, phoneNumber, verifier)
  }
}

/**
 * Sign in to Firebase using a custom token from the backend.
 * @param customToken Firebase custom token received from loginWithToken mutation
 * @returns Promise that resolves when sign-in is successful
 */
export const signInWithToken = async (customToken: string) => {
  if (isNotLocalDev) {
    return await signInWithCustomToken(auth, customToken)
  } else {
    // For local dev, we can mock the sign-in or handle it differently
    return Promise.resolve({ user: { uid: 'local-dev-user' } })
  }
}

/**
 * Get authorization header from Firebase tokens. This is used to authenticate with the GQL server.
 * At minimum, the AppCheck token is used, but if the user is logged in then the User token is also included.
 *
 * If the app is running in a local dev environment, a debug token is used instead of calling AppCheck.
 * @returns Authorization header for Firebase.
 */
export const getFirebaseAuthorizationHeader = async (): Promise<string> =>
  (await buildFirebaseAuthorizationHeader({
    getAppCheckToken: async () => {
      if (!isNotLocalDev) return import.meta.env.REACT_APP_CHECK_DEBUG_TOKEN
      const { token } = await getToken(appCheck, false)
      return token
    },
    // Read at t=0 now that the getters run concurrently. Safe because
    // AuthContextProvider gates all children on firebaseInitialized, so no
    // operation is issued before onAuthStateChanged fires.
    getUserToken: async () =>
      isFirebaseInitialized && auth.currentUser ? await auth.currentUser.getIdToken() : undefined,
    appCheckFallbackToken: APP_CHECK_FAILURE_TOKEN,
    onError: (stage, error) => {
      if (!(error instanceof Error)) return

      if (stage === 'appCheck') {
        logError('Error obtaining AppCheck token', { error })
        return
      }

      if (isIndexedDBError(error)) {
        indexedDBMonitor.triggerFailure(`Firebase token error: ${error.message}`)
      }
      logError('Error obtaining user token', { error })
    },
    // Unreachable: the fallback is a non-empty constant. Narrows the type only.
  })) ?? APP_CHECK_FAILURE_TOKEN
